import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, CalendarCheck, Banknote } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/19292921/pexels-photo-19292921.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

const LINES = [
  { text: "EVERY DRIVE", cls: "text-white" },
  { text: "BUILDS", cls: "text-white" },
  { text: "OWNERSHIP.", cls: "text-[#F57C00]" },
];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

const BADGES = [
  { icon: Banknote, text: "No credit card required" },
  { icon: CalendarCheck, text: "48–60 month ownership terms" },
  { icon: ShieldCheck, text: "Insured & tracked vehicles" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="min-h-[100vh] flex items-center relative overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1F4E] via-[#0B1F4E]/75 to-[#0B1F4E]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F4E] via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-8"
          data-testid="hero-overline"
        >
          Rentals · Rent to Own · Sales — Boksburg, East Rand
        </motion.p>

        <h1 className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-7xl lg:text-8xl mb-8">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
                className={`block ${line.cls}`}
                data-testid={`hero-headline-line-${i + 1}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="text-base md:text-lg text-slate-300 max-w-xl leading-relaxed mb-10"
          data-testid="hero-subcopy"
        >
          A reliable car today. A clear ownership plan for tomorrow. Built for
          employed and self-employed South Africans who don't fit the banks'
          boxes — with a visible price, a defined term and no undisclosed balloon.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.0 }}
          className="flex flex-wrap items-center gap-4 mb-14"
        >
          <Link
            to="/pre-qualify"
            data-testid="hero-prequalify-btn"
            className="bg-[#F57C00] hover:bg-[#E67300] text-white font-bold px-8 py-4 rounded-md transition-all duration-300 hover:-translate-y-1"
          >
            Check if you qualify
          </Link>
          <Link
            to="/vehicles"
            data-testid="hero-browse-btn"
            className="bg-transparent border-2 border-[#87CEEB] text-[#87CEEB] hover:bg-[#87CEEB] hover:text-[#0B1F4E] font-bold px-8 py-4 rounded-md transition-colors duration-300"
          >
            Browse vehicles
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="flex flex-wrap gap-x-8 gap-y-3"
        >
          {BADGES.map((b) => (
            <span key={b.text} className="flex items-center gap-2.5 text-sm text-slate-300" data-testid={`hero-badge-${b.text.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <b.icon size={18} className="text-[#F57C00]" />
              {b.text}
            </span>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
